/**
 * The shapes a survey answer is allowed to take, and a runtime gate that keeps them apart.
 *
 * Why this exists: SurveySession holds one `currentResponse` for every question type. Nothing
 * stopped a photo-upload object sitting in that slot when a text question was on screen, so it
 * got submitted as the respondent's prose — 186 times on PROD, plus 73 more once a voice
 * transcript was concatenated on top of it and stringified to "[object Object]".
 *
 * The union below is not invented: it is the set of shapes actually present in 868k stored
 * PROD answers, so `isValidAnswerFor` accepts real historical data and rejects only genuine
 * mismatches. Anything added here must be checked against the data first.
 */

export interface PhotoUploadAnswer {
  images: unknown[];
  all_extracted_products?: unknown[];
  text_fallback?: string;
}

/** A choice question where the respondent picked "Other" and typed their own text. */
export interface OtherChoiceAnswer {
  choice: string;
  other_text?: string;
}

/** An image-based choice question — the selection is by index/url, not label. */
export interface ImageChoiceAnswer {
  selected_index?: number;
  selected_indices?: number[];
  selected_urls?: string[];
}

/** Matrix rows and allocation buckets are keyed by their own arbitrary labels. */
export type KeyedScoreAnswer = Record<string, number | string>;

/** Emitted by the runner when branching logic skips a question — a marker, not an answer. */
export interface SkippedAnswer {
  skipped_reason: string;
}

export type AnswerValue =
  | string
  | number
  | string[]
  | PhotoUploadAnswer
  | OtherChoiceAnswer
  | ImageChoiceAnswer
  | KeyedScoreAnswer
  | SkippedAnswer
  | null;

const TEXT_QUESTION_TYPES = new Set(['long_text', 'short_text', 'numeric_input', 'yes_no', 'info_screen']);
const NUMERIC_QUESTION_TYPES = new Set(['rating_scale', 'nps']);
const KEYED_SCORE_TYPES = new Set(['matrix_rating', 'matrix_ratings', 'percentage_allocation']);
/** Multi-step types whose answer shape varies per set/scenario; not worth over-constraining. */
const FREEFORM_SHAPE_TYPES = new Set(['max_diff', 'conjoint_analysis', 'image_hotspot']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isSkipMarker(value: unknown): value is SkippedAnswer {
  return isPlainObject(value) && typeof value.skipped_reason === 'string';
}

export function isPhotoUploadAnswer(value: unknown): value is PhotoUploadAnswer {
  return isPlainObject(value) && Array.isArray(value.images);
}

/**
 * Does this value belong to this question type?
 *
 * Used both to enable Next and as a last gate before submit. It answers "is this the right KIND
 * of answer", not "is it complete" — per-type completeness (all matrix rows rated, allocation
 * sums to 100) stays in canProceed where it already lives.
 */
export function isValidAnswerFor(questionType: string | undefined, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  // A skip marker is legitimate for any type — the runner writes it, not the respondent.
  if (isSkipMarker(value)) return true;

  if (questionType && TEXT_QUESTION_TYPES.has(questionType)) {
    // The whole point: a text question accepts text and nothing else. A photo object, a matrix
    // map or an array reaching here is stale state from another question.
    return typeof value === 'string';
  }

  if (questionType && NUMERIC_QUESTION_TYPES.has(questionType)) {
    return typeof value === 'number' || (typeof value === 'string' && value.trim() !== '');
  }

  if (questionType === 'photo_upload') {
    return isPhotoUploadAnswer(value);
  }

  if (questionType === 'ranking') {
    return Array.isArray(value);
  }

  if (questionType === 'single_choice' || questionType === 'multiple_choice') {
    if (typeof value === 'string') return true;
    if (Array.isArray(value)) return true;
    if (!isPlainObject(value)) return false;
    // Only the two documented object shapes — explicitly NOT a photo object.
    return typeof value.choice === 'string'
      || 'selected_index' in value || 'selected_indices' in value || 'selected_urls' in value;
  }

  if (questionType && KEYED_SCORE_TYPES.has(questionType)) {
    return isPlainObject(value) && !isPhotoUploadAnswer(value);
  }

  if (questionType && FREEFORM_SHAPE_TYPES.has(questionType)) {
    return isPlainObject(value) || Array.isArray(value) || typeof value === 'string';
  }

  // Unknown/new question type: don't block the respondent, but still refuse the one shape we
  // know is always a leak from elsewhere.
  return !isPhotoUploadAnswer(value);
}

/** Human-readable reason a value was rejected, for logging when we drop one. */
export function describeAnswerMismatch(questionType: string | undefined, value: unknown): string {
  const actual = Array.isArray(value) ? 'array'
    : isPhotoUploadAnswer(value) ? 'photo-upload object'
    : isPlainObject(value) ? `object{${Object.keys(value).slice(0, 3).join(',')}}`
    : typeof value;
  return `${questionType ?? 'unknown'} question received ${actual}`;
}
